// Main Theme Configuration
// MUI v5 compatible - design tokens with CSS variable injection
import { createTheme, ThemeOptions } from '@mui/material/styles';
import { designTokens, dark, light } from './tokens';

const baseThemeOptions: Omit<ThemeOptions, 'palette'> = {
  breakpoints: {
    values: designTokens.breakpoints,
  },
  spacing: (factor: number) => `${designTokens.spacing[factor as keyof typeof designTokens.spacing] || factor * 4}px`,
  shape: {
    borderRadius: designTokens.borderRadius.default,
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily.base,
    h1: {
      fontFamily: designTokens.typography.fontFamily.heading,
      fontSize: designTokens.typography.fontSize['5xl'],
      fontWeight: designTokens.typography.fontWeight.extrabold,
    },
    h2: {
      fontFamily: designTokens.typography.fontFamily.heading,
      fontSize: designTokens.typography.fontSize['4xl'],
      fontWeight: designTokens.typography.fontWeight.bold,
    },
    h3: {
      fontFamily: designTokens.typography.fontFamily.heading,
      fontSize: designTokens.typography.fontSize['3xl'],
      fontWeight: designTokens.typography.fontWeight.semibold,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          // Inject all design tokens as CSS custom properties (works in any MUI version)
          ...Object.fromEntries(
            Object.entries({
              '--anime-token-brand-primary-main': designTokens.colors.brand.primary.main,
              '--anime-token-brand-primary-light': designTokens.colors.brand.primary.light,
              '--anime-token-brand-primary-dark': designTokens.colors.brand.primary.dark,
              '--anime-token-brand-secondary-main': designTokens.colors.brand.secondary.main,
              '--anime-token-brand-accent-main': designTokens.colors.brand.accent.main,
              '--anime-gradient-primary': 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
              '--anime-gradient-text-accent': 'linear-gradient(135deg, #8B5CF6, #EC4899)',
            })
          ),
        },
        '*': { boxSizing: 'border-box', margin: 0, padding: 0 },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          scrollBehavior: 'smooth',
        },
        '::selection': { backgroundColor: 'rgba(139, 92, 246, 0.25)' },
        '::-webkit-scrollbar': { width: 8, height: 8 },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.15)',
          borderRadius: 4,
        },
        ':focus-visible': { outline: `2px solid ${designTokens.colors.brand.primary.main}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'medium' },
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          textTransform: 'none',
          fontWeight: designTokens.typography.fontWeight.semibold,
        },
        contained: { boxShadow: designTokens.shadows.md },
        sizeLarge: { minHeight: 48 },
      },
      variants: [
        { props: { variant: 'soft' }, style: { backgroundColor: 'rgba(139, 92, 246, 0.12)', color: '#8B5CF6', border: '1px solid rgba(139, 92, 246, 0.25)' } },
        { props: { variant: 'ghost' }, style: { backgroundColor: 'transparent' } },
        { props: { variant: 'gradient' }, style: { background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', color: '#fff', '&:hover': { filter: 'brightness(1.1)' } } },
      ],
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius['2xl'],
          border: `1px solid ${designTokens.colors.border.default}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: designTokens.borderRadius.xl },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: designTokens.borderRadius.full },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, position: 'fixed' },
      styleOverrides: {
        root: { backgroundImage: 'none', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${designTokens.colors.border.default}` },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderRadius: 0 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: designTokens.borderRadius['2xl'] },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: designTokens.borderRadius.xl, backgroundImage: 'none' },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { borderRadius: designTokens.borderRadius.md, backdropFilter: 'blur(8px)' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: 999 },
      },
    },
  },
  shadows: [
    'none', designTokens.shadows.sm, designTokens.shadows.md, designTokens.shadows.md,
    designTokens.shadows.md, designTokens.shadows.lg, designTokens.shadows.lg,
    designTokens.shadows.lg, designTokens.shadows.xl, designTokens.shadows.xl,
    designTokens.shadows.xl, designTokens.shadows.xl, designTokens.shadows.xl,
    designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
    designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
    designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
    designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
  ] as ThemeOptions['shadows'],
};

export const darkTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'dark',
    primary: { main: designTokens.colors.brand.primary.main, light: designTokens.colors.brand.primary.light, dark: designTokens.colors.brand.primary.dark },
    secondary: { main: designTokens.colors.brand.secondary.main },
    background: {
      default: dark.background.default,
      paper: dark.background.paper,
    },
    text: {
      primary: dark.text.primary,
      secondary: dark.text.secondary,
    },
    divider: dark.divider,
  },
});

export const lightTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: { main: designTokens.colors.brand.primary.main, light: designTokens.colors.brand.primary.light, dark: designTokens.colors.brand.primary.dark },
    secondary: { main: designTokens.colors.brand.secondary.main },
    background: {
      default: light.background.default,
      paper: light.background.paper,
    },
    text: {
      primary: light.text.primary,
      secondary: light.text.secondary,
    },
    divider: light.divider,
  },
});

// Default theme (dark)
export const theme = darkTheme;

// ThemeProvider wrapper (backwards compatible)
export { ThemeProvider } from '@mui/material/styles';